// Adres do e-Doręczeń adresata — pokazany i uzupełniany TAM, gdzie pismo powstaje.
//
// Bez tego adres byłby problemem osoby wysyłającej na końcu łańcucha (CEO): pismo trafiłoby
// do kolejki bez adresata i ktoś inny musiałby go szukać. Autor pisma widzi tu od razu, czy
// urząd ma adres. Zapis idzie do rejestru urzędów (następny raz nikt już nie szuka), a ten
// poprawia tylko CEO / dyrektor techniczny — pozostali dostają samą informację.
import { useState } from "react";
import { Check, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { lookupEdeliveryAddress } from "@/features/edoreczenia/api";
import { useOfficeMutations } from "../../hooks";

export function EdeliveryRecipientHint({
  office,
}: {
  office: { id: string; name: string; edelivery_address: string; can_edit: boolean } | null;
}) {
  const { update } = useOfficeMutations();
  const [draft, setDraft] = useState("");
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState("");
  const [found, setFound] = useState<{ edelivery_address: string; name: string }[]>([]);

  if (!office) {
    return (
      <p className="text-[11px] text-[var(--color-primary)]">
        Wniosek nie ma wskazanego adresata — bez urzędu nie ma dokąd wysłać.
      </p>
    );
  }

  if (office.edelivery_address) {
    return (
      <p className="text-[11px] text-[var(--color-text-muted)]">
        Adresat: <b>{office.name}</b> ·{" "}
        <span className="font-mono">{office.edelivery_address}</span>
      </p>
    );
  }

  if (!office.can_edit) {
    return (
      <p className="max-w-lg text-[11px] text-[var(--color-primary)]">
        <b>{office.name}</b> nie ma jeszcze adresu do e-Doręczeń — uzupełnić go w rejestrze
        urzędów może dyrektor techniczny; bez niego pismo zostanie tylko odnotowane i trzeba je
        wysłać papierowo.
      </p>
    );
  }

  const search = async () => {
    setSearching(true);
    setMessage("");
    setFound([]);
    try {
      const r = await lookupEdeliveryAddress(office.name);
      if (!r.available) {
        setMessage(r.reason || "Wyszukiwarka operatora niedostępna — wpisz adres ręcznie.");
      } else if (!r.results.length) {
        setMessage("Rejestr operatora nie zna tego podmiotu — sprawdź nazwę albo wpisz adres ręcznie.");
      } else {
        setFound(r.results);
      }
    } catch {
      setMessage("Nie udało się odpytać rejestru operatora.");
    } finally {
      setSearching(false);
    }
  };

  const save = (value: string) => {
    const address = value.trim();
    if (!address) return;
    update.mutate(
      { id: office.id, payload: { edelivery_address: address } },
      { onSuccess: () => setMessage("Zapisano przy urzędzie.") },
    );
  };

  return (
    <div className="max-w-lg space-y-1">
      <p className="text-[11px] text-[var(--color-primary)]">
        <b>{office.name}</b> nie ma jeszcze adresu do e-Doręczeń — uzupełnij go tutaj, inaczej
        pismo zostanie tylko odnotowane i trzeba je wysłać papierowo.
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <input
          className="input-base w-64 font-mono text-xs"
          placeholder="AE:PL-…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button size="sm" onClick={() => save(draft)} disabled={!draft.trim() || update.isPending}>
          <Check size={13} className="mr-1" /> Zapisz przy urzędzie
        </Button>
        <Button size="sm" variant="secondary" onClick={search} loading={searching}>
          <Search size={13} className="mr-1" /> Szukaj w rejestrze
        </Button>
      </div>
      {found.map((f) => (
        <button
          key={f.edelivery_address}
          type="button"
          className="block w-full border border-[var(--color-border)] px-2 py-1 text-left text-xs hover:bg-[var(--color-bg-elevated)]"
          onClick={() => save(f.edelivery_address)}
        >
          <span className="font-mono">{f.edelivery_address}</span>{" "}
          <span className="text-[var(--color-text-muted)]">{f.name}</span>
        </button>
      ))}
      {message && <p className="text-[11px] text-[var(--color-text-muted)]">{message}</p>}
    </div>
  );
}
